/**
 * Users API Service Module
 *
 * Handles user profile, balance, ledger, and transaction operations.
 * All endpoints require Bearer token authentication (automatically injected).
 *
 * @module api/users
 * @example
 * ```typescript
 * import { usersAPI } from '@/api';
 *
 * // Get user's token balances
 * const balances = await usersAPI.getBalances();
 *
 * // Transfer tokens to another user
 * const txId = await usersAPI.transfer({
 *   target_username: 'bob',
 *   asset: 'uo',
 *   amount: '100.50'
 * });
 * ```
 */

import { api } from './client';
import type { StaticRewardPreview } from '../utils/static-reward-display';
import type {
  UserBalances,
  LedgerEntry,
  ListResponse,
  LedgerQueryParams,
  TransferRequest,
  TransferResponse,
  InternalConvertRequest,
  InternalConvertResponse,
} from './types';

/**
 * Users API endpoints
 */
export const usersAPI = {
  /**
   * GET /api/users/profile
   *
   * Fetch the current user's full profile information.
   *
   * @returns User profile with all metadata
   *
   * @example
   * ```typescript
   * const profile = await usersAPI.getProfile();
   * console.log(profile.username, profile.level_code);
   * ```
   *
   * @required_headers
   * - Authorization: "Bearer <access_token>" (automatically injected)
   *
   * @throws ApiError if token is invalid or expired
   */
  getProfile: async (params?: { account_id?: number }) =>
    api.get('/api/users/profile', params),

  /**
   * GET /api/users/balances
   *
   * Fetch all token balances for the current user.
   * Pass `account_id` to view a sub-account (master only).
   */
  getBalances: async (params?: { account_id?: number }): Promise<{
    balances: UserBalances;
    ap_queue?: string;
    user_id?: number;
    overview?: Record<string, string>;
    daily_perf?: import('../utils/wallet-daily-perf').WalletDailyPerf;
  }> =>
    api.get<{
      balances: UserBalances;
      ap_queue?: string;
      user_id?: number;
      overview?: Record<string, string>;
      daily_perf?: import('../utils/wallet-daily-perf').WalletDailyPerf;
    }>('/api/users/balances', params),

  /**
   * GET /api/users/ledger
   *
   * Fetch paginated ledger entries for the current user.
   * Optionally filter by specific token asset.
   *
   * @param params - Query parameters for pagination and filtering
   * @param params.page - Page number (1-indexed, defaults to 1)
   * @param params.limit - Items per page (defaults to 20)
   * @param params.asset - Optional token ID to filter by (e.g., 'uo', 'ep', 'iep')
   * @returns Paginated list of ledger entries
   *
   * @example
   * ```typescript
   * // Get first page of all ledger entries
   * const ledger1 = await usersAPI.getLedger({
   *   page: 1,
   *   limit: 20
   * });
   *
   * // Get ledger entries for specific token
   * const uoLedger = await usersAPI.getLedger({
   *   asset: 'uo',
   *   page: 1,
   *   limit: 50
   * });
   *
   * // Returns:
   * // {
   * //   total: 142,
   * //   rows: [
   * //     {
   * //       id: "tz_001",
   * //       asset: "uo",
   * //       quantity: "100.50",
   * //       tx_type: "transfer_out",
   * //       tx_id: "transfer_123",
   * //       memo: "Payment to alice",
   * //       timestamp: "2024-01-15T10:30:00Z",
   * //       create_time: "2024-01-15T10:30:00Z"
   * //     }
   * //   ]
   * // }
   * ```
   *
   * @required_headers
   * - Authorization: "Bearer <access_token>" (automatically injected)
   *
   * @throws ApiError if token is invalid or expired
   */
  getLedger: async (params?: LedgerQueryParams & { account_id?: number }): Promise<ListResponse<LedgerEntry>> =>
    api.get<ListResponse<LedgerEntry>>('/api/users/ledger', params),

  /**
   * POST /api/users/transfer
   *
   * Transfer tokens to another registered user.
   * The operation is atomic — both debit and credit succeed or both fail.
   *
   * @param request - Transfer details
   * @param request.target_username - Recipient's username
   * @param request.asset - Token ID (e.g., 'uo', 'ep', 'iep')
   * @param request.amount - Amount to transfer (as string for precision)
   * @returns Transaction details with ID
   *
   * @example
   * ```typescript
   * const transfer = await usersAPI.transfer({
   *   target_username: 'bob',
   *   asset: 'uo',
   *   amount: '100.50'
   * });
   *
   * // Returns:
   * // {
   * //   tx_id: "transfer_123abc",
   * //   from_id: 42,
   * //   to_id: 43,
   * //   asset: "uo",
   * //   amount: "100.50",
   * //   timestamp: "2024-01-15T10:30:00Z"
   * // }
   * ```
   *
   * @required_headers
   * - Authorization: "Bearer <access_token>" (automatically injected)
   * - Content-Type: "application/json" (automatically set)
   *
   * @throws ApiError if:
   * - Token is invalid or expired
   * - Insufficient balance
   * - Target user not found
   * - Invalid asset
   */
  transfer: async (request: {
    to_username: string;
    asset: string;
    amount: string;
    memo?: string;
    transaction_password?: string;
    security_answer?: string;
  }): Promise<TransferResponse> =>
    api.post<TransferResponse>('/api/users/transfer', request),

  /**
   * POST /api/users/convert
   *
   * Convert between supported token pairs.
   * Supported conversions: iEP ↔ EP, iEP ↔ UO, EP ↔ UO
   *
   * @param request - Conversion details
   * @param request.from_asset - Source token ID
   * @param request.to_asset - Destination token ID
   * @param request.from_amount - Amount to convert from source asset
   * @returns Conversion result with target amount
   *
   * @example
   * ```typescript
   * const convert = await usersAPI.convert({
   *   from_asset: 'iep',
   *   to_asset: 'ep',
   *   from_amount: '100'
   * });
   *
   * // Returns:
   * // {
   * //   from_asset: "iep",
   * //   to_asset: "ep",
   * //   from_amount: "100",
   * //   to_amount: "95.50",  // After conversion fee
   * //   tx_id: "convert_123abc"
   * // }
   * ```
   *
   * @required_headers
   * - Authorization: "Bearer <access_token>" (automatically injected)
   * - Content-Type: "application/json" (automatically set)
   *
   * @throws ApiError if:
   * - Token is invalid or expired
   * - Insufficient balance
   * - Invalid conversion pair
   * - From/to assets are the same
   */
  convert: async (request: {
    from_asset: string;
    to_asset: string;
    amount: string;
    transaction_password?: string;
    security_answer?: string;
    memo?: string;
    idempotency_key?: string;
  }): Promise<InternalConvertResponse> =>
    api.post<InternalConvertResponse>('/api/users/convert', request),

  /** PWA「資產變換」菜单 ↔ API — see `AssetConversionCatalog` in `./asset-conversion.ts` */
  getAssetConversion: () =>
    api.get<import('./asset-conversion').AssetConversionCatalog>('/api/users/asset-conversion'),

  convertRpSp3Preview: (params: { amount: string }) =>
    api.get<Record<string, unknown>>('/api/users/convert/rp-sp3/preview', params),

  convertRpSp3: (body: {
    amount: string;
    transaction_password: string;
    security_answer?: string;
    memo?: string;
    idempotency_key?: string;
  }) => api.post<Record<string, unknown>>('/api/users/convert/rp-sp3', body),

  convertMpUoPreview: () =>
    api.get<{
      mp_balance: string;
      uo_balance: string;
      convert_ratio: string;
      mp_per_uo: number;
    }>('/api/users/convert/mp-uo/preview'),

  convertMpUo: (body: {
    mp_amount: string;
    transaction_password: string;
    security_answer: string;
    memo?: string;
    idempotency_key?: string;
  }) => api.post<Record<string, unknown>>('/api/users/convert/mp-uo', body),

  bindWallet: (body: {
    wallet_address: string;
    transaction_password: string;
    security_answer?: string;
  }) => api.post<Record<string, unknown>>('/api/users/bind-wallet', body),

  listUsers: (params?: { cursor?: string; limit?: number }) =>
    api.get<Record<string, unknown>>('/api/users/list', params),

  /** POST /api/users/imputation — one-click sub-account consolidation */
  imputation: (body: Record<string, unknown>) =>
    api.post<Record<string, unknown>>('/api/users/imputation', body),

  /** GET /api/users/imputation/logs — 一键归集明细 */
  getImputationLogs: (params?: { page?: number; limit?: number }) =>
    api.get<{
      total: number;
      rows: Array<{
        id: string;
        create_time: string;
        ep: string;
        sp1: string;
        sp2: string;
        sp3: string;
        rp: string;
        mp: string;
        fp: string;
      }>;
    }>('/api/users/imputation/logs', params),

  /** GET /api/users/fork-record/latest — 分叉记录（只显示最后一个拆分） */
  getLatestForkRecord: () =>
    api.get<{
      record: {
        count: string;
        multiple: string;
        before_unit_price: string;
        after_unit_price: string;
        before_uo: string;
        after_uo: string;
        create_time: string;
      } | null;
    }>('/api/users/fork-record/latest'),

  /** GET /api/users/security — whether transaction password + security Q are configured */
  getSecurityStatus: () =>
    api.get<{ transaction_password_set: boolean; security_question: string | null }>(
      '/api/users/security',
    ),

  /** POST /api/users/security — first-time setup (transaction password + security Q) */
  setupSecurity: (body: {
    transaction_password: string;
    security_question: string;
    security_answer: string;
  }) => api.post<{ ok: boolean }>('/api/users/security', body),

  /** POST /api/users/security/verify-tx-password — unlock masked hang-buy/sell amounts */
  verifyTxPassword: (body: { transaction_password: string }) =>
    api.post<{ ok: boolean }>('/api/users/security/verify-tx-password', body),

  registerMemberPreview: (params?: {
    placement_user_id?: number;
    star_package?: 'E1' | 'E2' | 'E3';
  }) => api.get<Record<string, unknown>>('/api/users/register-member/preview', params),

  registerMember: (body: {
    username: string;
    email?: string;
    password: string;
    password_confirm: string;
    position: 'L' | 'R';
    placement_user_id?: number;
    star_package?: 'E1' | 'E2' | 'E3';
    phone?: string;
  }) => api.post<Record<string, unknown>>('/api/users/register-member', body),

  lineageTransferPreview: (params: { to_username: string }) =>
    api.get<Record<string, unknown>>('/api/users/lineage-transfer/preview', params),

  lineageContacts: () =>
    api.get<{
      upline: { username: string; nickname: string | null; level_code: string } | null;
      downlines: Array<{ username: string; nickname: string | null; level_code: string }>;
      sender_level_code: string;
      tp_fee_pct: string;
    }>('/api/users/lineage-transfer/contacts'),

  lineageTransfer: (body: {
    to_username: string;
    amount_rp?: string;
    amount_tp?: string;
    amount_fc?: string;
    transaction_password: string;
    security_answer: string;
    memo?: string;
  }) => api.post<Record<string, unknown>>('/api/users/lineage-transfer', body),

  lineageSingleTransferPreview: (params: { to_username: string; asset: 'tp' | 'fc' | 'rp' }) =>
    api.get<{
      asset: string;
      direction: 'up' | 'down';
      recipient: {
        id: number;
        username: string;
        nickname: string | null;
        level_code: string;
      };
      minimum: string;
      step: string;
      balance: string;
      sender_level_code: string;
      qualifies: boolean;
      fee_pct?: string;
      fee_preview?: { fee: string; net_to_recipient: string } | null;
    }>('/api/users/lineage-transfer/single/preview', params),

  lineageSingleTransfer: (body: {
    asset: 'tp' | 'fc' | 'rp';
    amount: string;
    to_username: string;
    transaction_password: string;
    security_answer: string;
    memo?: string;
  }) => api.post<Record<string, unknown>>('/api/users/lineage-transfer/single', body),

  getStaticRewardPreview: (params?: { account_id?: number }) =>
    api.get<StaticRewardPreview>('/api/users/static-reward/preview', params),

  getRevenueDetailsMonth: (month: string, params?: { account_id?: number }) =>
    api.get<{
      month: string;
      sum: string;
      list: Array<{ week: string; start: string; end: string; ep: string }>;
    }>('/api/users/revenue-details/month', { month, ...params }),

  getRevenueDetailsWeek: (
    params: {
      start: string;
      end: string;
      page?: number;
      limit?: number;
      account_id?: number;
    },
  ) =>
    api.get<{
      total: number;
      page: number;
      limit: number;
      rows: Array<{ remark: string; ep: string; create_time: string }>;
    }>('/api/users/revenue-details/week', params),
};
